import { api } from '@/lib/api/client'
import type { MetricSource, Plan, PortfolioRollup } from '@/types/planning'

interface Collection<T> {
  data: T[]
}

export async function fetchPlans(params: Record<string, string | number> = {}): Promise<Plan[]> {
  const { data } = await api.get<Collection<Plan>>('/plans', { params })

  return data.data
}

export async function fetchPlan(id: string): Promise<Plan> {
  const { data } = await api.get<{ data: Plan }>(`/plans/${id}`)

  return data.data
}

export async function fetchMetricSources(): Promise<MetricSource[]> {
  const { data } = await api.get<Collection<MetricSource>>('/plans/metrics')

  return data.data
}

export interface NewPlan {
  divisionId?: string | null
  horizon: 'annual' | 'quarter'
  periodYear: number
  periodQuarter?: number | null
  name?: string
}

export async function createPlan(input: NewPlan): Promise<Plan> {
  const { data } = await api.post<{ data: Plan }>('/plans', input)

  return data.data
}

/**
 * A committed plan is immutable, so every write against one carries the reason
 * it moved. The API records a revision; the client just has to ask for it.
 */
export async function commitPlan(id: string): Promise<Plan> {
  const { data } = await api.post<{ data: Plan }>(`/plans/${id}/commit`)

  return data.data
}

export async function closePlan(id: string): Promise<Plan> {
  const { data } = await api.post<{ data: Plan }>(`/plans/${id}/close`)

  return data.data
}

export async function addObjective(
  planId: string,
  input: { title: string; reason?: string },
): Promise<string> {
  const { data } = await api.post<{ data: { id: string } }>(`/plans/${planId}/objectives`, input)

  return data.data.id
}

export async function deleteObjective(
  planId: string,
  objectiveId: string,
  reason?: string,
): Promise<void> {
  await api.delete(`/plans/${planId}/objectives/${objectiveId}`, {
    data: reason === undefined ? {} : { reason },
  })
}

export interface NewKeyResult {
  title: string
  source?: string
  unit?: string | null
  baseline?: number | null
  target: number
  current?: number | null
  reason?: string
}

export async function addKeyResult(
  planId: string,
  objectiveId: string,
  input: NewKeyResult,
): Promise<string> {
  const { data } = await api.post<{ data: { id: string } }>(
    `/plans/${planId}/objectives/${objectiveId}/key-results`,
    input,
  )

  return data.data.id
}

export async function addCommitment(
  planId: string,
  input: { projectId: string; committedDays: number; reason?: string },
): Promise<Plan> {
  const { data } = await api.post<{ data: Plan }>(`/plans/${planId}/commitments`, input)

  return data.data
}

export async function updateCommitment(
  planId: string,
  commitmentId: string,
  input: { committedDays?: number; reason?: string },
): Promise<Plan> {
  const { data } = await api.patch<{ data: Plan }>(
    `/plans/${planId}/commitments/${commitmentId}`,
    input,
  )

  return data.data
}

export async function removeCommitment(
  planId: string,
  commitmentId: string,
  reason?: string,
): Promise<void> {
  await api.delete(`/plans/${planId}/commitments/${commitmentId}`, {
    data: reason === undefined ? {} : { reason },
  })
}

export async function fetchRollup(stalledAfter?: number): Promise<PortfolioRollup> {
  const { data } = await api.get<{ data: PortfolioRollup }>('/portfolio/rollup', {
    params: stalledAfter === undefined ? {} : { stalledAfter },
  })

  return data.data
}

/**
 * Amending an objective or a key result.
 *
 * Creation and deletion were wired; correction was not, so a mistyped target
 * could only be removed and rebuilt — which loses the check-ins recorded
 * against it.
 */
export async function updateObjective(
  planId: string,
  objectiveId: string,
  changes: { title?: string; narrative?: string | null; ownerUserId?: string | null },
): Promise<void> {
  await api.patch(`/plans/${planId}/objectives/${objectiveId}`, changes)
}

export async function updateKeyResult(
  planId: string,
  objectiveId: string,
  keyResultId: string,
  changes: { title?: string; unit?: string | null; baseline?: number | null; target?: number | null },
): Promise<void> {
  await api.patch(`/plans/${planId}/objectives/${objectiveId}/key-results/${keyResultId}`, changes)
}

export async function deleteKeyResult(
  planId: string,
  objectiveId: string,
  keyResultId: string,
): Promise<void> {
  await api.delete(`/plans/${planId}/objectives/${objectiveId}/key-results/${keyResultId}`)
}

/** One committed version of a plan, as it stood when it was committed. */
export async function fetchPlanRevision(planId: string, revisionId: string): Promise<unknown> {
  const { data } = await api.get<{ data: unknown }>(`/plans/${planId}/revisions/${revisionId}`)

  return data.data
}
